import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Empty, Input, Modal, Select, Skeleton, Tag } from 'antd';
import type {
  AccountSummary,
  CapabilityBaseline,
  CentralIdentityCandidate,
  DynamicRole,
  IdentityBindingResponse,
  PageKey,
} from '@youkuai/contracts';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Icon } from './icons.tsx';
import { useAuth } from './auth.tsx';
import {
  bindCentralIdentity,
  changeAccountStatus,
  changeDynamicRoleStatus,
  cloneDynamicRole,
  completeIdentityBinding,
  createAccount,
  createDynamicRole,
  getAccessCatalog,
  listAccounts,
  listCentralIdentities,
  listDynamicRoles,
  replaceAccountDynamicRoles,
  replaceDynamicRolePages,
  resetIdentityCredential,
  updateAccount,
  updateDynamicRole,
} from './dynamic-access-api.ts';
import { listPeople } from './people-api.ts';
import { ApiRequestError } from './project-api.ts';

type Tab = 'accounts' | 'roles';
type Dialog =
  | { kind: 'account-create' }
  | { kind: 'account-edit'; account: AccountSummary }
  | { kind: 'account-roles'; account: AccountSummary }
  | { kind: 'account-status'; account: AccountSummary }
  | { kind: 'account-identity-bind'; account: AccountSummary }
  | { kind: 'account-identity-reset'; account: AccountSummary }
  | { kind: 'account-central-bind'; account: AccountSummary }
  | { kind: 'role-create' }
  | { kind: 'role-clone'; role: DynamicRole }
  | { kind: 'role-edit'; role: DynamicRole }
  | { kind: 'role-pages'; role: DynamicRole }
  | { kind: 'role-status'; role: DynamicRole };

const baselineLabels: Record<CapabilityBaseline, string> = {
  READ_ONLY: '只读', EXECUTOR: '执行', CONFIRMER: '确认', PLANNER: '内容策划', MANAGER: '负责人', FINANCE: '财务', SYSTEM_ADMIN: '系统管理员',
  // BUG-0184：发布专员基线在动态角色页的展示名。
  PUBLISHER: '发布专员',
};

function failureText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      LAST_ADMIN_REQUIRED: '系统必须保留至少一个可登录的系统管理员账号。',
      ROLE_IN_USE: '这个角色仍有关联账号，请先完成账号改派。',
      PROTECTED_ROLE: '系统管理员是受保护角色，不能停用或缩减权限。',
      VERSION_CONFLICT: '数据已被其他人更新，请刷新后再试。',
      ROLE_CODE_CONFLICT: '角色编码已存在，请使用其他编码。',
      ROLE_INACTIVE: '选择的角色已停用，请重新选择。',
      PERSON_NOT_FOUND: '没有找到这份人员档案，或人员已经停用。',
      PERSON_ACCOUNT_CONFLICT: '这份人员档案已经关联了登录账号。',
      USERNAME_CONFLICT: '这个登录名已经被使用，请换一个名称。',
      FORBIDDEN: '当前账号没有系统管理权限。',
      IDENTITY_ALREADY_LINKED: '这个账号已经绑定登录身份，无需重复开通。',
      IDENTITY_NOT_LINKED: '这个账号还没有绑定登录身份，请先完成身份绑定。',
      IDENTITY_PROVISIONING_UNAVAILABLE: '身份开通服务当前不可用，请联系系统管理员检查配置。',
      CENTRAL_DIRECTORY_UNAVAILABLE: '统一用户目录当前不可用，请稍后重试。',
      CENTRAL_IDENTITY_NOT_FOUND: '这个统一用户已不在当前可用目录中，请刷新后重新选择。',
      CENTRAL_IDENTITY_ALREADY_BOUND: '这个统一用户已经绑定了其他正式人员账号。',
      CENTRAL_IDENTITY_CONFLICT: '绑定存在账号或历史关系冲突，系统没有改写任何数据，请先人工核对。',
    };
    return messages[error.code] ?? `操作失败（${error.code}）`;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

function statusPill(status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE', protectedRole = false) {
  if (protectedRole) return <span className="access-status is-active"><i />系统内置</span>;
  const active = status === 'ACTIVE';
  return <span className={`access-status ${active ? 'is-active' : 'is-paused'}`}><i />{active ? '正常使用' : '已暂停'}</span>;
}

// navigator.clipboard requires a secure context; this test environment is served over plain HTTP.
function copyToClipboard(text: string): boolean {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
    return true;
  }
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try { copied = document.execCommand('copy'); } catch { copied = false; }
  document.body.removeChild(textarea);
  return copied;
}

export function DynamicAccessPage({ initialTab }: { initialTab: Tab }) {
  const { state } = useAuth();
  const navigate = useNavigate();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : '';
  const client = useQueryClient();
  const tab = initialTab;
  const [accountSearch, setAccountSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState<'ALL' | 'ACTIVE' | 'SUSPENDED'>('ALL');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedPages, setSelectedPages] = useState<PageKey[]>([]);
  const [accountPersonId, setAccountPersonId] = useState('');
  const [accountUsername, setAccountUsername] = useState('');
  const [accountNote, setAccountNote] = useState('');
  const [selectedCentralUserId, setSelectedCentralUserId] = useState('');
  const [reason, setReason] = useState('');
  const [bindingResult, setBindingResult] = useState<{ displayName: string; username: string; temporaryPassword: string } | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [roleCode, setRoleCode] = useState('');
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [baseline, setBaseline] = useState<Exclude<CapabilityBaseline, 'SYSTEM_ADMIN'>>('READ_ONLY');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const accounts = useQuery({ queryKey: ['dynamic-accounts', accountId], queryFn: () => listAccounts(token),
    enabled: state.kind === 'ready' });
  const roles = useQuery({ queryKey: ['dynamic-roles', accountId], queryFn: () => listDynamicRoles(token),
    enabled: state.kind === 'ready' });
  const catalog = useQuery({ queryKey: ['access-catalog', accountId], queryFn: () => getAccessCatalog(token),
    enabled: state.kind === 'ready' });
  const people = useQuery({ queryKey: ['people-for-accounts', accountId], queryFn: () => listPeople(token),
    enabled: state.kind === 'ready' && tab === 'accounts' });
  const centralIdentities = useQuery({ queryKey: ['central-identities', accountId],
    queryFn: () => listCentralIdentities(token), enabled: state.kind === 'ready' && tab === 'accounts' });

  const mutation = useMutation({
    mutationFn: (action: () => Promise<unknown>) => action(),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['dynamic-accounts', accountId] }),
        client.invalidateQueries({ queryKey: ['dynamic-roles', accountId] }),
        client.invalidateQueries({ queryKey: ['people-for-accounts', accountId] }),
        client.invalidateQueries({ queryKey: ['central-identities', accountId] }),
      ]);
      setDialog(null);
      setNotice({ type: 'success', text: '变更已保存；人员档案、项目职责和历史业务记录没有被改写。' });
    },
    onError: (error) => setNotice({ type: 'error', text: failureText(error) }),
  });

  const visibleAccounts = useMemo(() => {
    const query = accountSearch.trim().toLowerCase();
    return (accounts.data?.items ?? []).filter((account) =>
      (accountFilter === 'ALL' || account.status === accountFilter) &&
      (!query || `${account.displayName} ${account.username} ${account.personCode} ${account.roles.map((role) => role.name).join(' ')}`
        .toLowerCase().includes(query)));
  }, [accounts.data, accountFilter, accountSearch]);
  const selectedAccount = visibleAccounts.find((account) => account.accountId === selectedAccountId) ?? visibleAccounts[0] ?? null;
  const selectedRole = roles.data?.items.find((role) => role.roleId === selectedRoleId) ?? roles.data?.items[0] ?? null;
  const activeAccounts = accounts.data?.items.filter((account) => account.status === 'ACTIVE' && account.identityLinked).length ?? 0;
  const activeRoles = roles.data?.items.filter((role) => role.status === 'ACTIVE').length ?? 0;
  const customRoles = roles.data?.items.filter((role) => role.kind === 'CUSTOM').length ?? 0;
  const grantCount = roles.data?.items.reduce((total, role) => total + role.pageKeys.length, 0) ?? 0;
  const linkedPeople = new Set(accounts.data?.items.map((account) => account.personId) ?? []).size;
  const roleMap = new Map(roles.data?.items.map((role) => [role.roleId, role]) ?? []);
  const availablePeople = people.data?.items.filter((person) => person.active && !person.account) ?? [];
  const centralBindingByAccount = new Map((centralIdentities.data?.items ?? [])
    .filter((identity) => identity.boundAccountId)
    .map((identity) => [identity.boundAccountId!, identity]));

  function eligibleCentralIdentities(account: AccountSummary): CentralIdentityCandidate[] {
    return [...(centralIdentities.data?.items ?? [])].filter((identity) =>
      identity.boundAccountId === account.accountId || identity.boundAccountId === null ||
      identity.boundPersonCode?.startsWith('CU-'))
      .sort((left, right) => {
        const leftSuggested = left.suggestedAccountIds.includes(account.accountId) ? 1 : 0;
        const rightSuggested = right.suggestedAccountIds.includes(account.accountId) ? 1 : 0;
        return rightSuggested - leftSuggested || left.displayName.localeCompare(right.displayName, 'zh-CN');
      });
  }

  function resetDialog(next: Dialog) {
    setNotice(null);
    setReason('');
    if (next.kind === 'account-central-bind') {
      setSelectedCentralUserId('');
    }
    setSelectedIds(next.kind === 'account-roles' || next.kind === 'account-edit'
      ? next.account.roles.map((role) => role.roleId)
      : next.kind === 'account-create' ? roles.data?.items.filter((role) => role.status === 'ACTIVE' && !role.protected)
        .slice(0, 1).map((role) => role.roleId) ?? [] : []);
    setSelectedPages(next.kind === 'role-pages' ? [...next.role.pageKeys]
      : next.kind === 'role-create' ? ['WORKSPACE'] : []);
    if (next.kind === 'account-create') {
      setAccountPersonId(availablePeople[0]?.personId ?? '');
      setAccountUsername('');
      setAccountNote('');
    } else if (next.kind === 'account-edit') {
      setAccountPersonId(next.account.personId);
      setAccountUsername(next.account.username);
      setAccountNote(next.account.note ?? '');
    } else if (next.kind === 'role-edit') {
      setRoleCode(next.role.code);
      setRoleName(next.role.name);
      setRoleDescription(next.role.description ?? '');
      if (next.role.capabilityBaseline !== 'SYSTEM_ADMIN') setBaseline(next.role.capabilityBaseline);
    } else if (next.kind === 'role-clone') {
      setRoleCode(`${next.role.code}_COPY`);
      setRoleName(`${next.role.name}副本`);
      setRoleDescription(next.role.description ?? '');
    } else if (next.kind === 'role-create') {
      setRoleCode(''); setRoleName(''); setRoleDescription(''); setBaseline('READ_ONLY');
    }
    setDialog(next);
  }

  async function submitDialog() {
    if (!dialog || reason.trim().length < 2) return;
    setNotice(null);
    let refreshOwnSession = false;
    if (dialog.kind === 'account-create') {
      if (!accountPersonId || !selectedIds.length) return;
      await mutation.mutateAsync(() => createAccount(token, { personId: accountPersonId,
        username: accountUsername.trim().toLowerCase(), roleIds: selectedIds,
        note: accountNote.trim() || null, reason: reason.trim() }));
      setNotice({ type: 'success', text: '账号资料已建立；绑定登录身份后即可使用，不会自动加入任何项目。' });
    } else if (dialog.kind === 'account-edit') {
      await mutation.mutateAsync(() => updateAccount(token, dialog.account.accountId, {
        expectedVersion: dialog.account.version, username: accountUsername.trim().toLowerCase(),
        note: accountNote.trim() || null, reason: reason.trim(),
      }));
      refreshOwnSession = dialog.account.accountId === accountId;
    } else if (dialog.kind === 'account-roles') {
      if (!selectedIds.length) return;
      await mutation.mutateAsync(() => replaceAccountDynamicRoles(token, dialog.account.accountId, {
        expectedVersion: dialog.account.version, roleIds: selectedIds, reason: reason.trim(),
      }));
      refreshOwnSession = dialog.account.accountId === accountId;
    } else if (dialog.kind === 'account-status') {
      await mutation.mutateAsync(() => changeAccountStatus(token, dialog.account.accountId, {
        expectedVersion: dialog.account.version,
        status: dialog.account.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE', reason: reason.trim(),
      }));
      refreshOwnSession = dialog.account.accountId === accountId;
    } else if (dialog.kind === 'account-identity-bind') {
      const binding = await mutation.mutateAsync(
        () => completeIdentityBinding(token, dialog.account.accountId, { reason: reason.trim() }),
      ) as IdentityBindingResponse;
      if (binding.temporaryPassword) {
        setBindingResult({ displayName: dialog.account.displayName, username: dialog.account.username,
          temporaryPassword: binding.temporaryPassword });
      }
    } else if (dialog.kind === 'account-identity-reset') {
      const reset = await mutation.mutateAsync(
        () => resetIdentityCredential(token, dialog.account.accountId, { reason: reason.trim() }),
      ) as IdentityBindingResponse;
      if (reset.temporaryPassword) {
        setBindingResult({ displayName: dialog.account.displayName, username: dialog.account.username,
          temporaryPassword: reset.temporaryPassword });
      }
    } else if (dialog.kind === 'account-central-bind') {
      if (!selectedCentralUserId) return;
      await mutation.mutateAsync(() => bindCentralIdentity(token, dialog.account.accountId, {
        centralUserId: selectedCentralUserId, expectedVersion: dialog.account.version, reason: reason.trim(),
      }));
      setNotice({ type: 'success', text: '统一用户已绑定；对方首次登录时会自动连接到这份人员档案。' });
    } else if (dialog.kind === 'role-pages') {
      if (!selectedPages.length) return;
      await mutation.mutateAsync(() => replaceDynamicRolePages(token, dialog.role.roleId, {
        expectedVersion: dialog.role.version, pageKeys: selectedPages, reason: reason.trim(),
      }));
      refreshOwnSession = state.kind === 'ready' && state.session.roleAssignments.some((role) => role.roleId === dialog.role.roleId);
    } else if (dialog.kind === 'role-status') {
      await mutation.mutateAsync(() => changeDynamicRoleStatus(token, dialog.role.roleId, {
        expectedVersion: dialog.role.version,
        status: dialog.role.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE', reason: reason.trim(),
      }));
      refreshOwnSession = state.kind === 'ready' && state.session.roleAssignments.some((role) => role.roleId === dialog.role.roleId);
    } else if (dialog.kind === 'role-clone') {
      await mutation.mutateAsync(() => cloneDynamicRole(token, dialog.role.roleId, {
        code: roleCode, name: roleName, reason: reason.trim(),
      }));
    } else if (dialog.kind === 'role-edit') {
      await mutation.mutateAsync(() => updateDynamicRole(token, dialog.role.roleId, {
        expectedVersion: dialog.role.version, name: roleName, description: roleDescription.trim() || null,
        capabilityBaseline: baseline, reason: reason.trim(),
      }));
      refreshOwnSession = state.kind === 'ready' && state.session.roleAssignments.some((role) => role.roleId === dialog.role.roleId);
    } else {
      if (!selectedPages.length) return;
      await mutation.mutateAsync(() => createDynamicRole(token, {
        code: roleCode, name: roleName, description: roleDescription.trim() || null,
        capabilityBaseline: baseline, pageKeys: selectedPages, reason: reason.trim(),
      }));
    }
    if (refreshOwnSession) window.location.reload();
  }

  const loading = accounts.isPending || roles.isPending || catalog.isPending || (tab === 'accounts' && people.isPending);
  const failed = accounts.error ?? roles.error ?? catalog.error ?? people.error;
  const accountPages = selectedAccount ? new Set(selectedAccount.roles.flatMap((assignment) => roleMap.get(assignment.roleId)?.pageKeys ?? [])) : new Set<PageKey>();
  const linkedAccounts = selectedRole ? accounts.data?.items.filter((account) =>
    account.roles.some((assignment) => assignment.roleId === selectedRole.roleId)) ?? [] : [];
  const selectedBaseline = catalog.data?.capabilityBaselines.find((item) =>
    item.code === selectedRole?.capabilityBaseline);
  const selectedCentralIdentity = selectedAccount ? centralBindingByAccount.get(selectedAccount.accountId) : undefined;

  return <main className="page-shell access-page">
    <header className="access-page-head">
      <div><span className="access-eyebrow">SYSTEM ACCESS</span><h1>系统管理</h1>
        <p>账号决定谁能登录，角色决定能进入哪些页面；项目职责仍在具体项目中独立维护。</p></div>
      <section className="access-summary" aria-label="系统访问摘要">
        <div><span>登录账号</span><strong>{accounts.data?.items.length ?? 0}</strong><small>{activeAccounts} 个正常使用</small></div>
        <div><span>关联人员</span><strong>{linkedPeople}</strong><small>一人最多一个账号</small></div>
        <div><span>启用角色</span><strong>{activeRoles}</strong><small>{customRoles} 个自定义角色</small></div>
        <div><span>页面授权</span><strong>{grantCount}</strong><small>按角色集中配置</small></div>
      </section>
    </header>

    <nav className="access-tabs" aria-label="系统管理页面">
      <Link className={tab === 'accounts' ? 'is-active' : ''} to="/system/accounts"><span>账号与访问</span><b>{activeAccounts}</b></Link>
      <Link to="/system/external-invitations"><span>外部邀请</span><b>→</b></Link>
      <Link className={tab === 'roles' ? 'is-active' : ''} to="/system/roles"><span>角色与页面权限</span><b>{activeRoles}</b></Link>
      <Link to="/system/mcp-access"><span>MCP 权限</span><b>→</b></Link>
    </nav>

    {notice && <Alert className="access-notice" showIcon type={notice.type} title={notice.text} closable onClose={() => setNotice(null)} />}
    {failed && <Alert className="access-notice" showIcon type="error" title={failureText(failed)} />}
    {loading ? <Skeleton active paragraph={{ rows: 10 }} /> : tab === 'accounts' ? <>
      <section className="access-toolbar">
        <div className="access-segmented">{(['ALL', 'ACTIVE', 'SUSPENDED'] as const).map((value) =>
          <button key={value} className={accountFilter === value ? 'is-active' : ''} onClick={() => setAccountFilter(value)}>
            {value === 'ALL' ? '全部' : value === 'ACTIVE' ? '正常使用' : '已暂停'}</button>)}</div>
        <div className="access-toolbar-actions"><Input.Search allowClear value={accountSearch} onChange={(event) => setAccountSearch(event.target.value)}
          placeholder="搜索人员、账号或角色" /><Button type="primary" onClick={() => resetDialog({ kind: 'account-create' })}>新增账号</Button></div>
      </section>
      <section className="access-workspace">
        <aside className="access-index"><header><span><b>{visibleAccounts.length}</b> 个登录账号</span><small>登录身份不等于项目成员</small></header>
          <div>{visibleAccounts.map((account) => <button key={account.accountId}
            className={selectedAccount?.accountId === account.accountId ? 'is-active' : ''}
            onClick={() => setSelectedAccountId(account.accountId)}><i>{account.displayName.slice(0, 1)}</i><span>
              <small>@{account.username} · {account.personCode}</small><strong>{account.displayName}</strong>
              <em>{account.roles.map((role) => role.name).join('、') || '尚未分配角色'}</em></span>
              <span>{statusPill(account.status)}<small>{centralBindingByAccount.has(account.accountId)
                ? account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleDateString('zh-CN') : '统一身份已绑定'
                : account.identityLinked ? '备用身份已开通' : '待绑定统一身份'}</small></span></button>)}
            {!visibleAccounts.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有匹配的账号" />}</div></aside>
        <article className="access-inspector">{selectedAccount ? <>
          <header className="access-inspector-head"><div><i>{selectedAccount.displayName.slice(0, 1)}</i><span>
            <small>@{selectedAccount.username} · {selectedAccount.roles.map((role) => role.name).join(' / ') || '未设置角色'}</small>
            <h2>{selectedAccount.displayName}</h2><p>{selectedAccount.personCode}</p></span></div><div>{statusPill(selectedAccount.status)}
            <span className={`access-identity ${selectedCentralIdentity || selectedAccount.identityLinked ? 'is-linked' : 'is-pending'}`}>
              {selectedCentralIdentity ? '统一身份已绑定' : selectedAccount.identityLinked ? '备用身份已开通' : '待绑定统一身份'}</span>
            {!selectedCentralIdentity &&
              <Button type="primary" disabled={centralIdentities.isPending || !!centralIdentities.error}
                onClick={() => resetDialog({ kind: 'account-central-bind', account: selectedAccount })}>
                绑定统一用户</Button>}
            {!selectedCentralIdentity && !selectedAccount.identityLinked &&
              <Button onClick={() => resetDialog({ kind: 'account-identity-bind', account: selectedAccount })}>
                开通备用密码</Button>}
            {!selectedCentralIdentity && selectedAccount.identityLinked &&
              <Button onClick={() => resetDialog({ kind: 'account-identity-reset', account: selectedAccount })}>
                重置密码</Button>}
            <Button onClick={() => resetDialog({ kind: 'account-edit', account: selectedAccount })}>编辑账号</Button>
            <Button onClick={() => resetDialog({ kind: 'account-roles', account: selectedAccount })}>调整角色</Button>
            <Button danger={selectedAccount.status === 'ACTIVE'} onClick={() => resetDialog({ kind: 'account-status', account: selectedAccount })}>
              {selectedAccount.status === 'ACTIVE' ? '暂停账号' : '恢复账号'}</Button></div></header>
          <div className="access-inspector-body">
            <section className={`access-hero ${selectedAccount.status === 'ACTIVE' ? 'is-active' : 'is-paused'}`}><i><Icon name="user" size={16} /></i><span><small>LOGIN ACCESS</small>
              <h3>{selectedAccount.status !== 'ACTIVE' ? '当前账号已停止登录'
                : selectedCentralIdentity ? `已绑定${selectedCentralIdentity.kind === 'INTERNAL' ? '内部员工' : '外部协作者'} ${selectedCentralIdentity.displayName}`
                  : selectedAccount.identityLinked ? `当前叠加 ${selectedAccount.roles.length} 个角色` : '账号资料已建立，等待绑定统一用户'}</h3>
              <p>{selectedAccount.status !== 'ACTIVE' ? '暂停只阻止登录，人员档案、项目关系与历史记录全部保留。'
                : selectedCentralIdentity ? '首次登录时，系统会用稳定的统一用户 ID 自动接上当前账号，不再按姓名或邮箱猜测。'
                  : selectedAccount.identityLinked ? `可进入 ${accountPages.size} 个页面；具体项目内操作仍受成员职责与业务状态限制。`
                    : '请从统一用户目录中人工确认本人；绑定不会改变项目、任务、费用或历史记录。'}</p></span><strong>{accountPages.size}<small>授权页面</small></strong></section>
            <section className="access-facts"><div><span>关联人员档案</span><strong>{selectedAccount.displayName}</strong><small>{selectedAccount.personCode}</small></div>
              <div><span>当前角色</span><strong>{selectedAccount.roles.length}</strong><small>{selectedAccount.roles.map((role) => role.name).join('、') || '尚未设置'}</small></div>
              <div><span>登录身份</span><strong>{selectedCentralIdentity ? (selectedCentralIdentity.kind === 'INTERNAL' ? '内部员工' : '外部协作者') : selectedAccount.identityLinked ? '备用密码' : '待绑定'}</strong><small>{selectedCentralIdentity?.departmentName ?? `账号版本 ${selectedAccount.version}`}</small></div>
              <div><span>最近登录</span><strong>{selectedAccount.lastLoginAt ? new Date(selectedAccount.lastLoginAt).toLocaleDateString('zh-CN') : '尚未登录'}</strong><small>创建于 {new Date(selectedAccount.createdAt).toLocaleDateString('zh-CN')}</small></div></section>
            <section className="access-page-list"><header><div><span className="access-eyebrow">PAGE ACCESS</span><h3>当前可访问页面</h3></div><Link to="/system/roles">从角色调整</Link></header>
              <div>{catalog.data?.pages.map((page) => <article key={page.key} className={accountPages.has(page.key) ? 'is-granted' : ''}><i>{accountPages.has(page.key) ? <Icon name="check" size={13} /> : '—'}</i><span><b>{page.label}</b><small>{page.key.startsWith('PROJECT_') ? '项目内页面' : '全局页面'}</small></span></article>)}</div></section>
            <section className="access-rule"><i><Icon name="info" size={14} /></i><span><b>账号、人员档案和项目职责相互独立</b><small>调整角色不会把人员加入或移出项目，也不会改写任务、交付、费用和结算历史。</small></span></section>
          </div></> : <Empty description="选择一个账号查看访问范围" />}</article>
      </section>
    </> : <>
      <section className="access-toolbar role-toolbar"><div><span className="access-eyebrow">ROLE DIRECTORY</span><strong>{roles.data?.items.length ?? 0} 个角色 · {activeRoles} 个启用</strong>
        <small>系统管理员受保护，业务角色可以按实际组织新增、复制和停用。</small></div>
        <Button type="primary" onClick={() => resetDialog({ kind: 'role-create' })}>新增角色</Button></section>
      <section className="access-workspace">
        <aside className="access-index role-index"><header><span><b>{roles.data?.items.length ?? 0}</b> 个全局角色</span><small>包含预置与自定义</small></header>
          <div>{roles.data?.items.map((role) => <button key={role.roleId}
            className={`${selectedRole?.roleId === role.roleId ? 'is-active' : ''} ${role.status === 'INACTIVE' ? 'is-paused' : ''}`}
            onClick={() => setSelectedRoleId(role.roleId)}><i>{role.name.slice(0, 1)}</i><span><small>{role.code}</small><strong>{role.name}</strong>
              <em>{role.kind === 'CUSTOM' ? '自定义' : '预置'} · {role.assignedAccountCount} 个账号</em></span><span><strong>{role.pageKeys.length}</strong><small>页面</small></span></button>)}</div>
          <footer><i><Icon name="info" size={14} /></i><span>角色控制页面与能力上限；项目成员职责仍在具体项目中维护。</span></footer></aside>
        <article className="access-inspector">{selectedRole ? <><header className="access-inspector-head"><div><i>{selectedRole.name.slice(0, 1)}</i><span>
          <small>{selectedRole.code} · {selectedRole.kind === 'CUSTOM' ? 'CUSTOM ROLE' : 'BUILT-IN ROLE'}</small><h2>{selectedRole.name}</h2>
          <p>{selectedRole.description ?? '暂无角色说明'}</p></span></div><div>{statusPill(selectedRole.status, selectedRole.protected)}
          <Tag>{baselineLabels[selectedRole.capabilityBaseline]}</Tag><Button onClick={() => resetDialog({ kind: 'role-clone', role: selectedRole })}>复制</Button>
          {!selectedRole.protected && <><Button onClick={() => resetDialog({ kind: 'role-edit', role: selectedRole })}>编辑</Button>
            <Button danger={selectedRole.status === 'ACTIVE'} onClick={() => resetDialog({ kind: 'role-status', role: selectedRole })}>{selectedRole.status === 'ACTIVE' ? '停用' : '恢复'}</Button>
            <Button type="primary" onClick={() => resetDialog({ kind: 'role-pages', role: selectedRole })}>页面权限</Button></>}</div></header>
          <div className="access-inspector-body"><section className="access-hero role-principle"><i>{selectedRole.protected ? '◆' : '⌁'}</i><span><small>ACCESS PRINCIPLE</small>
            <h3>页面权限 ∩ 能力权限，共同决定可执行操作</h3>
            <p>{selectedRole.protected ? '系统管理员固定保留全部页面和操作能力；业务状态与审计规则仍然生效。' : `只有页面已授权、“${baselineLabels[selectedRole.capabilityBaseline]}”基线包含该操作，且对象范围和业务状态同时允许时，操作才会生效。`}</p></span></section>
            <section className="access-capability-list"><header><div><span className="access-eyebrow">ACTION CAPABILITIES</span><h3>可执行操作</h3>
              <p>{selectedBaseline?.description}</p></div><strong>{selectedBaseline?.capabilities.length ?? 0}<small>项能力</small></strong></header>
              <div>{selectedBaseline?.capabilities.map((capability) => <article key={capability.code}><span><b>{capability.label}</b><small>{capability.code}</small></span><em>{capability.group}</em></article>)}</div>
              <footer><i><Icon name="info" size={14} /></i><span><b>页面授权不代表可执行页面内的所有操作</b><small>账号叠加多个角色时，页面与能力分别取并集，再取交集；项目职责、本人关系和业务状态仍可进一步限制操作。</small></span></footer>
            </section>
            {(['GLOBAL', 'PROJECT'] as const).map((group) => <section className="access-page-group" key={group}><header><div><h3>{group === 'GLOBAL' ? '全局页面' : '项目内页面'}</h3>
              <p>{group === 'GLOBAL' ? '从主导航进入的独立业务空间' : '进入项目后显示的业务页面'}</p></div><small>{catalog.data?.pages.filter((page) => (group === 'PROJECT') === page.key.startsWith('PROJECT_') && selectedRole.pageKeys.includes(page.key)).length} 已授权</small></header>
              <div>{catalog.data?.pages.filter((page) => (group === 'PROJECT') === page.key.startsWith('PROJECT_')).map((page) => <article key={page.key} className={selectedRole.pageKeys.includes(page.key) ? 'is-granted' : ''}>
                <i>{selectedRole.pageKeys.includes(page.key) ? <Icon name="check" size={13} /> : '—'}</i><span><b>{page.label}</b><small>{page.key}</small></span><em>{selectedRole.pageKeys.includes(page.key) ? '可访问' : '不可访问'}</em></article>)}</div></section>)}
            <section className="access-linked"><header><div><span className="access-eyebrow">LINKED ACCOUNTS</span><h3>使用该角色的账号</h3></div><small>{linkedAccounts.length} 个账号</small></header>
              <div>{linkedAccounts.map((account) => <button key={account.accountId} onClick={() => { setSelectedAccountId(account.accountId); navigate('/system/accounts'); }}><i>{account.displayName.slice(0, 1)}</i><span><b>{account.displayName}</b><small>@{account.username}</small></span>{statusPill(account.status)}</button>)}
                {!linkedAccounts.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前没有账号使用这个角色" />}</div></section>
            <section className="access-rule"><i><Icon name="info" size={14} /></i><span><b>权限变更只影响后续访问</b><small>不会修改人员资料、项目职责，也不会改写已经发生的任务、交付、费用与结算记录。</small></span></section>
          </div></> : <Empty description="尚未建立角色" />}</article>
      </section>
    </>}

    <Modal open={!!dialog} onCancel={() => setDialog(null)} onOk={() => void submitDialog()}
      confirmLoading={mutation.isPending} okText="保存变更" cancelText="取消" width={dialog?.kind === 'role-pages' || dialog?.kind === 'role-create' ? 760 : 560}
      okButtonProps={{ disabled: reason.trim().length < 2 ||
        (dialog?.kind === 'account-central-bind' && !selectedCentralUserId) ||
        ((dialog?.kind === 'account-create' || dialog?.kind === 'account-edit') &&
          (!accountPersonId || !/^[a-z0-9][a-z0-9._-]{2,63}$/.test(accountUsername.trim().toLowerCase()) ||
            (dialog.kind === 'account-create' && !selectedIds.length))) ||
        (dialog?.kind === 'account-roles' && !selectedIds.length) ||
        ((dialog?.kind === 'role-pages' || dialog?.kind === 'role-create') && !selectedPages.length) }}
      title={dialog?.kind === 'account-create' ? '新增登录账号'
        : dialog?.kind === 'account-edit' ? `编辑 ${dialog.account.displayName} 的账号`
        : dialog?.kind === 'account-roles' ? `调整 ${dialog.account.displayName} 的角色`
        : dialog?.kind === 'account-status' ? `${dialog.account.status === 'ACTIVE' ? '暂停' : '恢复'} ${dialog.account.displayName} 的账号`
        : dialog?.kind === 'account-central-bind' ? `绑定 ${dialog.account.displayName} 的统一用户身份`
        : dialog?.kind === 'account-identity-bind' ? `为 ${dialog.account.displayName} 完成身份绑定`
        : dialog?.kind === 'account-identity-reset' ? `重置 ${dialog.account.displayName} 的登录密码`
        : dialog?.kind === 'role-create' ? '新增自定义角色'
        : dialog?.kind === 'role-clone' ? `复制 ${dialog.role.name}`
        : dialog?.kind === 'role-edit' ? `编辑 ${dialog.role.name}`
        : dialog?.kind === 'role-pages' ? `编辑 ${dialog.role.name} 的页面权限`
        : dialog?.kind === 'role-status' ? `${dialog.role.status === 'ACTIVE' ? '停用' : '恢复'} ${dialog.role.name}` : ''}>
      {(dialog?.kind === 'account-create' || dialog?.kind === 'account-edit') && <div className="access-dialog">
        <p>账号必须关联人员档案，但不会因此加入项目。内部员工和外部协作者都应绑定稳定的统一身份。</p>
        <div className="access-account-fields"><label><span>关联人员档案</span><Select showSearch optionFilterProp="label"
          disabled={dialog.kind === 'account-edit'} value={accountPersonId || null}
          onChange={(value: string) => setAccountPersonId(value)}
          placeholder={availablePeople.length ? '选择尚未创建账号的人员' : '暂无可关联人员'}
          options={(dialog.kind === 'account-edit' ? [{ personId: dialog.account.personId,
            displayName: dialog.account.displayName, personCode: dialog.account.personCode }] : availablePeople)
            .map((person) => ({ value: person.personId, label: `${person.displayName} · ${person.personCode}` }))} /></label>
          <label><span>登录名</span><div className="access-username-input"><i><Icon name="user" size={14} /></i><Input maxLength={64} value={accountUsername}
            onChange={(event) => setAccountUsername(event.target.value.toLowerCase())} placeholder="例如 lin.jian" /></div></label>
          <label className="is-wide"><span>账号备注</span><Input.TextArea rows={2} maxLength={1000} value={accountNote}
            onChange={(event) => setAccountNote(event.target.value)} placeholder="说明主要用途，可不填" /></label></div>
        {dialog.kind === 'account-create' && <><span className="access-field-title">初始角色</span>
          <Checkbox.Group value={selectedIds} onChange={(values) => setSelectedIds(values as string[])}>
            <div className="access-role-picker">{roles.data?.items.filter((role) => role.status === 'ACTIVE').map((role) =>
              <Checkbox key={role.roleId} value={role.roleId}><span><b>{role.name}</b><small>{baselineLabels[role.capabilityBaseline]} · {role.pageKeys.length} 个页面</small></span></Checkbox>)}</div>
          </Checkbox.Group></>}
        <section className="access-binding-note"><i><Icon name="info" size={14} /></i><span><b>{dialog.kind === 'account-create' ? '创建后可能处于“身份待开通”' : '编辑资料不会改写身份绑定'}</b>
          <small>{dialog.kind === 'account-create' ? '创建后请人工选择对应统一用户；系统只使用稳定用户 ID 连接，不按姓名自动合并。' : '登录身份、安全凭据和历史审计记录均保持不变。'}</small></span></section>
      </div>}
      {(dialog?.kind === 'account-roles') && <div className="access-dialog"><p>一个账号可以叠加多个角色；页面和能力取并集，但不会扩大项目对象范围。</p>
        <Checkbox.Group value={selectedIds} onChange={(values) => setSelectedIds(values as string[])}>
          <div className="access-role-picker">{roles.data?.items.filter((role) => role.status === 'ACTIVE' || selectedIds.includes(role.roleId)).map((role) =>
            <Checkbox key={role.roleId} value={role.roleId}><span><b>{role.name}</b><small>{baselineLabels[role.capabilityBaseline]} · {role.pageKeys.length} 个页面</small></span></Checkbox>)}</div>
        </Checkbox.Group></div>}
      {(dialog?.kind === 'role-pages' || dialog?.kind === 'role-create') && <div className="access-dialog"><p>页面权限只决定导航入口和路由访问，具体业务操作仍受能力基线与对象职责限制。</p>
        {dialog.kind === 'role-create' && <RoleFields code={roleCode} setCode={setRoleCode} name={roleName} setName={setRoleName}
          description={roleDescription} setDescription={setRoleDescription} baseline={baseline} setBaseline={setBaseline} />}
        <Checkbox.Group value={selectedPages} onChange={(values) => setSelectedPages(values as PageKey[])}><div className="access-page-picker">
          {catalog.data?.pages.map((page) => <Checkbox key={page.key} value={page.key}><span><b>{page.label}</b><small>{page.key.startsWith('PROJECT_') ? '项目内页面' : '全局页面'}</small></span></Checkbox>)}</div></Checkbox.Group></div>}
      {(dialog?.kind === 'role-edit' || dialog?.kind === 'role-clone') && <div className="access-dialog"><p>{dialog.kind === 'role-clone' ? '复制会保留原角色的能力基线和页面范围，再以新编码建立独立角色。' : '修改能力基线会立即影响该角色账号后续允许执行的操作。'}</p>
        <RoleFields code={roleCode} setCode={setRoleCode} name={roleName} setName={setRoleName}
          description={roleDescription} setDescription={setRoleDescription} baseline={baseline} setBaseline={setBaseline}
          clone={dialog.kind === 'role-clone'} editing={dialog.kind === 'role-edit'} /></div>}
      {(dialog?.kind === 'account-status' || dialog?.kind === 'role-status') && <div className="access-dialog access-confirm">
        <strong>{dialog.kind === 'account-status' ? '账号启停不会改变人员与项目关系' : '停用角色前必须先完成所有账号改派'}</strong>
        <p>{dialog.kind === 'account-status' ? '暂停后不能继续登录，但人员档案、项目职责、任务、费用和历史记录全部保留。' : '恢复角色后，仍需将角色重新分配给账号才会产生访问权限。'}</p></div>}
      {dialog?.kind === 'account-central-bind' && <div className="access-dialog">
        <p>请人工确认统一用户与“{dialog.account.displayName}”确为同一个人。系统不会仅凭同名自动绑定。</p>
        <label><span className="access-field-title">统一用户</span><Select showSearch optionFilterProp="label"
          value={selectedCentralUserId || null} onChange={(value: string) => setSelectedCentralUserId(value)}
          placeholder="选择已同步的内部员工或外部协作者" style={{ width: '100%' }}
          options={eligibleCentralIdentities(dialog.account).map((identity) => ({
            value: identity.centralUserId,
            label: `${identity.displayName}${identity.departmentName ? ` · ${identity.departmentName}` : ''}${identity.email ? ` · ${identity.email}` : ''}${identity.suggestedAccountIds.includes(dialog.account.accountId) ? ' · 姓名匹配' : ''}`,
          }))} /></label>
        <section className="access-binding-note"><i><Icon name="info" size={14} /></i><span><b>绑定后使用统一身份登录</b>
          <small>若同步曾建立临时空档案，系统会在无业务历史时安全合并；项目、任务、费用和结算仍保留在当前人员档案。</small></span></section>
      </div>}
      {dialog?.kind === 'account-identity-bind' && <div className="access-dialog access-confirm">
        <strong>将为 @{dialog.account.username} 建立备用密码登录身份</strong>
        <p>系统会自动生成一个临时密码，首次登录时需要修改。密码只会在本次操作完成后显示一次，请当面安全地转交给使用者。</p></div>}
      {dialog?.kind === 'account-identity-reset' && <div className="access-dialog access-confirm">
        <strong>将为 @{dialog.account.username} 生成一个新的临时密码</strong>
        <p>原密码立即失效，新密码首次登录时需要修改。密码只会在本次操作完成后显示一次，请当面安全地转交给使用者。</p></div>}
      {dialog && <label className="access-reason"><span>变更原因 <b>*</b></span><Input.TextArea rows={3} maxLength={500} value={reason}
        onChange={(event) => setReason(event.target.value)} placeholder="说明本次权限或账号变更的业务原因" /></label>}
    </Modal>

    <Modal open={!!bindingResult} onCancel={() => { setBindingResult(null); setCopyFeedback(null); }}
      footer={<Button type="primary" onClick={() => { setBindingResult(null); setCopyFeedback(null); }}>我已记下这个密码</Button>}
      title={`${bindingResult?.displayName ?? ''} 的登录凭证`} closable={false} maskClosable={false}>
      {bindingResult && <div className="access-dialog access-identity-secret">
        <p>请立即将以下凭证通过安全渠道转交给使用者。关闭此窗口后，密码将不再显示，也不会以明文形式保存。</p>
        <div className="access-identity-secret-row"><span>登录名</span><code>@{bindingResult.username}</code></div>
        <div className="access-identity-secret-row"><span>临时密码</span><code>{bindingResult.temporaryPassword}</code>
          <Button size="small" onClick={() => setCopyFeedback(copyToClipboard(bindingResult.temporaryPassword)
            ? '已复制到剪贴板。' : '当前环境不支持自动复制，请手动选中密码文本复制。')}>复制</Button></div>
        {copyFeedback && <small className="access-copy-feedback">{copyFeedback}</small>}
        <section className="access-binding-note"><i><Icon name="info" size={14} /></i><span><b>首次登录需要设置新密码</b>
          <small>Keycloak 会强制使用者在首次登录时修改密码，系统不保存任何明文密码。</small></span></section>
      </div>}
    </Modal>
  </main>;
}

function RoleFields(props: {
  code: string; setCode(value: string): void; name: string; setName(value: string): void;
  description: string; setDescription(value: string): void;
  baseline: Exclude<CapabilityBaseline, 'SYSTEM_ADMIN'>;
  setBaseline(value: Exclude<CapabilityBaseline, 'SYSTEM_ADMIN'>): void;
  clone?: boolean; editing?: boolean;
}) {
  return <div className="access-role-fields"><label><span>角色编码</span><Input value={props.code} {...(props.editing ? { disabled: true } : {})}
    onChange={(event) => props.setCode(event.target.value.toUpperCase())} placeholder="例如 VIDEO_REVIEWER" /></label>
    <label><span>角色名称</span><Input value={props.name} onChange={(event) => props.setName(event.target.value)} placeholder="例如 视频审核" /></label>
    {!props.clone && <label><span>能力基线</span><Select value={props.baseline} onChange={props.setBaseline} options={([
      'READ_ONLY', 'EXECUTOR', 'CONFIRMER', 'PLANNER', 'MANAGER', 'FINANCE',
    ] as const).map((value) => ({ value, label: baselineLabels[value] }))} /></label>}
    {!props.clone && <label className="is-wide"><span>角色说明</span><Input.TextArea rows={2} value={props.description}
      onChange={(event) => props.setDescription(event.target.value)} placeholder="说明这个角色在组织中的使用范围" /></label>}
  </div>;
}
